import { api, ApiClientError, type LaunchPlanDocument } from './api';
import { readStoredLaunchPlans, removeStoredLaunchPlan, writeStoredLaunchPlan, type StoredLaunchPlan } from './launch-plan-store';

const projectSlug = 'angelmyth';

class LaunchPlanClient {
  private documents = new Map<string, LaunchPlanDocument>();
  private storageRecords = new Map<string, StoredLaunchPlan>();
  private currentUserId = '';
  private currentUserRole = '';

  setCurrentUser(userId: string, role = ''): void {
    this.currentUserId = userId;
    this.currentUserRole = role;
  }
  getCurrentUserId(): string { return this.currentUserId; }

  isReadOnlyUser(): boolean {
    return this.currentUserRole === 'viewer' || this.currentUserRole === 'readonly';
  }

  canManagePlans(): boolean {
    return this.currentUserRole === 'admin' || this.currentUserRole === 'creator' || this.currentUserId === 'angelmyth-creator';
  }

  heartbeat(planId: string, path = '*'): Promise<void> {
    return api.heartbeatLaunchPlan(projectSlug, planId, path).then(() => undefined);
  }

  async activeEditors(planId: string, path = '*'): Promise<Array<{ userId: string; displayName: string; path: string; lastSeen: string }>> {
    return (await api.getLaunchPlanPresence(projectSlug, planId, path)).editors;
  }

  clearPresence(planId: string, path = '*'): Promise<void> {
    return api.clearLaunchPlanPresence(projectSlug, planId, path).then(() => undefined);
  }

  async list(): Promise<Record<string, unknown>[]> {
    const cached = await readStoredLaunchPlans();
    this.storageRecords = new Map(cached.map(record => [record.id, record]));
    try {
      const { plans } = await api.getLaunchPlans(projectSlug);
      const remoteIds = new Set(plans.map(plan => plan.id));
      for (const plan of plans) {
        const local = this.storageRecords.get(plan.id);
        // A pending browser draft must keep the revision and base snapshot from
        // the moment it was edited. Re-basing that old draft onto the newest
        // revision makes it look authoritative and can overwrite newer team
        // content. Submit it with its original base so the API performs a real
        // three-way merge instead.
        const record: StoredLaunchPlan = local?.pending
          ? await this.syncPendingRecord(local, plan)
          : { id: plan.id, document: plan, pending: false, serverExists: true, baseData:structuredClone(plan.data) };
        this.storageRecords.set(plan.id, record);
        await writeStoredLaunchPlan(record);
      }
      for (const record of cached) {
        if (!record.pending && !remoteIds.has(record.id)) {
          this.storageRecords.delete(record.id);
          await removeStoredLaunchPlan(record.id);
        }
      }
    } catch (error) {
      if (!this.storageRecords.size) throw error;
      console.warn('Launch plans are being loaded from the local cache', error);
    }
    this.documents = new Map([...this.storageRecords.values()].map(record => [record.id, record.document]));
    return [...this.storageRecords.values()]
      .sort((left, right) => right.document.updatedAt.localeCompare(left.document.updatedAt))
      .map(record => this.decorate(record.document, record.pending));
  }

  async save(data: Record<string, unknown>): Promise<{ data: Record<string, unknown>; autoMerged: boolean; resolvedConflicts: string[]; savedOffline: boolean; sessionExpired: boolean }> {
    const id = String(data.id ?? '');
    const existing = this.documents.get(id);
    const existingRecord = this.storageRecords.get(id);
    const uploadedImageUrls: string[] = [];
    try {
      const cleanData = await this.uploadEmbeddedImages(this.clean(data), uploadedImageUrls);
      const baseData = existingRecord?.baseData ?? existing?.data ?? {};
      const saved = existing && existingRecord?.serverExists !== false
        ? await api.updateLaunchPlan(projectSlug, id, existing.revision, baseData, cleanData)
        : await api.createLaunchPlan(projectSlug, cleanData);
      this.documents.set(saved.id, saved);
      const record: StoredLaunchPlan = { id: saved.id, document: saved, pending: false, serverExists: true, baseData:structuredClone(saved.data) };
      this.storageRecords.set(saved.id, record);
      await writeStoredLaunchPlan(record);
      return { data: this.decorate(saved), autoMerged: Boolean(saved.autoMerged), resolvedConflicts: saved.resolvedConflicts ?? [], savedOffline: false, sessionExpired: false };
    } catch (error) {
      await Promise.all(uploadedImageUrls.map(url => api.deleteAssetByUrl(url).catch(() => undefined)));
      if (error instanceof ApiClientError && error.status === 409) throw new Error('多位同事正在同时保存，请稍后再点一次保存。');
      if (!isOfflineError(error)) throw error;
      const sessionExpired = error instanceof ApiClientError && error.status === 401;
      const now = new Date().toISOString();
      const localDocument: LaunchPlanDocument = {
        id,
        revision: existing?.revision ?? 0,
        data: this.clean(data),
        createdBy: existing?.createdBy ?? this.currentUserId,
        ...(existing?.creatorName ? { creatorName: existing.creatorName } : {}),
        updatedBy: this.currentUserId,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      };
      const record: StoredLaunchPlan = {
        id,
        document: localDocument,
        pending: true,
        serverExists: existingRecord?.serverExists ?? Boolean(existing),
        baseData:existingRecord?.baseData ?? structuredClone(existing?.data ?? {}),
      };
      this.documents.set(id, localDocument);
      this.storageRecords.set(id, record);
      await writeStoredLaunchPlan(record);
      return { data: this.decorate(localDocument, true), autoMerged: false, resolvedConflicts: [], savedOffline: true, sessionExpired };
    }
  }

  async duplicate(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    return (await this.save(data)).data;
  }

  async remove(id: string): Promise<void> {
    const stored = this.storageRecords.get(id);
    if (stored?.serverExists !== false) {
      try {
        await api.deleteLaunchPlan(projectSlug, id);
      } catch (error) {
        // A stale browser cache can outlive its server record. Treat a missing
        // remote plan as already deleted so the local ghost card is removed.
        if (!(error instanceof ApiClientError && error.status === 404)) throw error;
      }
    }
    this.documents.delete(id);
    this.storageRecords.delete(id);
    await removeStoredLaunchPlan(id);
  }

  private decorate(document: LaunchPlanDocument, pending = false): Record<string, unknown> {
    return {
      ...structuredClone(document.data),
      _collaboration: {
        revision: document.revision,
        createdBy: document.createdBy,
        creatorName: document.creatorName,
        updatedAt: document.updatedAt,
        // 删除是高风险操作，仅管理员可执行；同事仍可共同编辑并保存企划内容。
        canCreate: this.canManagePlans(),
        canDelete: this.canManagePlans(),
        pending,
      },
    };
  }

  private clean(data: Record<string, unknown>): Record<string, unknown> {
    const clone = structuredClone(data);
    delete clone._collaboration;
    return clone;
  }

  private async syncPendingRecord(local: StoredLaunchPlan, remote: LaunchPlanDocument): Promise<StoredLaunchPlan> {
    const uploadedImageUrls: string[] = [];
    try {
      const cleanData = await this.uploadEmbeddedImages(this.clean(local.document.data), uploadedImageUrls);
      const saved = await api.updateLaunchPlan(
        projectSlug,
        remote.id,
        local.document.revision,
        local.baseData ?? {},
        cleanData,
      );
      return {
        id: saved.id,
        document: saved,
        pending: false,
        serverExists: true,
        baseData: structuredClone(saved.data),
      };
    } catch (error) {
      await Promise.all(uploadedImageUrls.map(url => api.deleteAssetByUrl(url).catch(() => undefined)));
      console.warn('Pending launch-plan draft could not be synchronized; preserving its original merge base', error);
      return { ...local, serverExists: true };
    }
  }

  private async uploadEmbeddedImages(data: Record<string, unknown>, uploadedImageUrls: string[]): Promise<Record<string, unknown>> {
    const clone = structuredClone(data);
    await visit(clone, []);
    return clone;

    async function visit(value: unknown, path: Array<string | number>): Promise<void> {
      if (Array.isArray(value)) {
        for (let index = 0; index < value.length; index += 1) {
          const item = value[index];
          if (typeof item === 'string' && item.startsWith('data:image/')) {
            const preserveOriginal = isOriginalProductImagePath(path);
            value[index] = await api.uploadImage(
              dataUrlToFile(item, launchImageFilename(item, index, preserveOriginal)),
              projectSlug,
              { preserveOriginal },
            );
            uploadedImageUrls.push(String(value[index]));
          } else await visit(item, [...path, index]);
        }
        return;
      }
      if (typeof value !== 'object' || value === null) return;
      for (const [key, child] of Object.entries(value)) {
        const childPath = [...path, key];
        if (typeof child === 'string' && child.startsWith('data:image/')) {
          const preserveOriginal = isOriginalProductImagePath(childPath);
          (value as Record<string, unknown>)[key] = await api.uploadImage(
            dataUrlToFile(child, launchImageFilename(child, 0, preserveOriginal)),
            projectSlug,
            { preserveOriginal },
          );
          uploadedImageUrls.push(String((value as Record<string, unknown>)[key]));
        } else await visit(child, childPath);
      }
    }
  }
}

export const launchPlanClient = new LaunchPlanClient();

function isOfflineError(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof ApiClientError && (error.status === 401 || error.status >= 500));
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const match = /^data:([^;,]+);base64,(.*)$/.exec(dataUrl);
  if (!match?.[1] || !match[2]) throw new Error('图片缓存格式无法识别');
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new File([bytes], filename, { type: match[1] });
}

function isOriginalProductImagePath(path: Array<string | number>): boolean {
  const keys = path.filter((part): part is string => typeof part === 'string');
  const leaf = keys.at(-1);
  if (leaf === 'finalImages' || leaf === 'wearImages') return true;
  if (leaf === 'images' && (keys.includes('basicProducts') || keys.includes('subwaveBasicProducts'))) return true;
  if (leaf === 'image' && (keys.includes('homepageFeaturedProducts') || keys.includes('offsiteFeaturedProducts'))) return true;
  return false;
}

function launchImageFilename(dataUrl: string, index: number, preserveOriginal: boolean): string {
  const mimeType = /^data:([^;,]+);base64,/.exec(dataUrl)?.[1] ?? 'image/webp';
  const extension = mimeType === 'image/png' ? 'png' : mimeType === 'image/jpeg' ? 'jpg' : 'webp';
  return `launch-${preserveOriginal ? 'product-original' : 'reference'}-${index}.${extension}`;
}
